import React, { useEffect, useState, useCallback } from 'react';
import {
  Building2, Loader2, LogOut, Search, TrendingUp, Wallet, Users, CircleDollarSign,
  MoreHorizontal, ShieldOff, ShieldCheck, BadgeCheck, RefreshCw,
  Cpu, Coins, Percent, Save, RotateCcw,
} from 'lucide-react';
import api from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Separator } from '@/components/ui/separator';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuLabel,
} from '@/components/ui/dropdown-menu';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { toast } from 'sonner';

const rpCompact = (n) => {
  n = Number(n || 0);
  if (n >= 1e9) return 'Rp ' + (n / 1e9).toFixed(1).replace('.', ',') + ' M';
  if (n >= 1e6) return 'Rp ' + (n / 1e6).toFixed(1).replace('.', ',') + ' jt';
  if (n >= 1e3) return 'Rp ' + Math.round(n / 1e3) + ' rb';
  return 'Rp ' + n;
};
const rpFull = (n) => 'Rp ' + Number(n || 0).toLocaleString('id-ID');

const marginTone = (p) => (p >= 70 ? 'text-emerald-400' : p >= 40 ? 'text-amber-400' : 'text-red-400');
const TASK_LABEL = {
  quote: 'RAB / Penawaran', chat: 'Chat Insight', whatsapp: 'WhatsApp',
  followup: 'Follow-up', cutting: 'Optimasi Potong',
};

const PLAN_BADGE = {
  free: 'border-slate-600 bg-slate-700/50 text-slate-200',
  pro: 'border-cyan-500/40 bg-cyan-500/15 text-cyan-300',
  business: 'border-amber-500/40 bg-amber-500/15 text-amber-300',
};
const STATUS_BADGE = {
  trial: 'border-sky-500/40 bg-sky-500/15 text-sky-300',
  active: 'border-emerald-500/40 bg-emerald-500/15 text-emerald-300',
  expired: 'border-orange-500/40 bg-orange-500/15 text-orange-300',
  suspended: 'border-red-500/40 bg-red-500/15 text-red-300',
};
const STATUS_LABEL = { trial: 'Trial', active: 'Aktif', expired: 'Expired', suspended: 'Suspended' };
const PLAN_LABEL = { free: 'Gratis', pro: 'Pro', business: 'Bisnis' };

function Kpi({ icon: Icon, label, value, sub, testid }) {
  return (
    <div data-testid={testid} className="rounded-2xl border border-border bg-card p-4">
      <div className="flex items-center gap-2 text-xs text-muted-foreground">
        <Icon className="h-4 w-4" /> {label}
      </div>
      <div className="mt-2 font-display text-2xl font-bold tabular-nums">{value}</div>
      {sub && <div className="mt-0.5 text-xs text-muted-foreground">{sub}</div>}
    </div>
  );
}

function TenantDetailDialog({ orgId, open, onClose, onChanged }) {
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [quotaInput, setQuotaInput] = useState('');

  useEffect(() => {
    if (!open || !orgId) return;
    setLoading(true);
    api.get(`/admin/tenants/${orgId}`).then((r) => setD(r.data)).finally(() => setLoading(false));
  }, [open, orgId]);

  useEffect(() => {
    if (d) setQuotaInput(d.ai_quota_override == null ? '' : String(d.ai_quota_override));
  }, [d]);

  const refresh = async () => { const r = await api.get(`/admin/tenants/${orgId}`); setD(r.data); onChanged?.(); };

  const setPlan = async (plan) => {
    setBusy(true);
    try {
      await api.patch(`/admin/tenants/${orgId}/plan`, { plan });
      toast.success(`Paket diubah ke ${PLAN_LABEL[plan]}`);
      await refresh();
    } catch (e) { !e?.__gated && toast.error(e?.response?.data?.detail || 'Gagal'); } finally { setBusy(false); }
  };
  const toggleSuspend = async () => {
    setBusy(true);
    try {
      await api.patch(`/admin/tenants/${orgId}/status`, { suspended: !d.suspended });
      toast.success(d.suspended ? 'Workshop diaktifkan' : 'Workshop ditangguhkan');
      await refresh();
    } catch (e) { !e?.__gated && toast.error(e?.response?.data?.detail || 'Gagal'); } finally { setBusy(false); }
  };
  const saveQuota = async () => {
    const raw = quotaInput.trim();
    const q = raw === '' ? null : parseInt(raw, 10);
    if (q !== null && (Number.isNaN(q) || q < -1)) { toast.error('Kuota tidak valid (kosong, -1, atau angka ≥ 0).'); return; }
    setBusy(true);
    try {
      await api.patch(`/admin/tenants/${orgId}/ai-quota`, { quota: q });
      toast.success(q === null ? 'Kuota AI direset ke default paket' : 'Kuota AI diperbarui');
      await refresh();
    } catch (e) { !e?.__gated && toast.error(e?.response?.data?.detail || 'Gagal'); } finally { setBusy(false); }
  };
  const resetQuota = async () => {
    setQuotaInput('');
    setBusy(true);
    try {
      await api.patch(`/admin/tenants/${orgId}/ai-quota`, { quota: null });
      toast.success('Kuota AI direset ke default paket');
      await refresh();
    } catch (e) { !e?.__gated && toast.error(e?.response?.data?.detail || 'Gagal'); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader><DialogTitle className="font-display">{loading ? 'Memuat…' : d?.nama}</DialogTitle></DialogHeader>
        {loading || !d ? (
          <div className="space-y-3 py-4"><Skeleton className="h-20 w-full" /><Skeleton className="h-32 w-full" /></div>
        ) : (
          <Tabs defaultValue="ringkasan" data-testid="admin-tenant-detail-tabs">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="ringkasan">Ringkasan</TabsTrigger>
              <TabsTrigger value="anggota">Anggota ({d.members?.length || 0})</TabsTrigger>
              <TabsTrigger value="langganan">Langganan</TabsTrigger>
            </TabsList>
            <TabsContent value="ringkasan" className="space-y-3 pt-3">
              <div className="flex flex-wrap gap-2">
                <Badge variant="outline" className={`rounded-full ${PLAN_BADGE[d.plan]}`}>{PLAN_LABEL[d.plan]}</Badge>
                <Badge variant="outline" className={`rounded-full ${STATUS_BADGE[d.status]}`}>{STATUS_LABEL[d.status]}</Badge>
                <Badge variant="outline" className="rounded-full">MRR {rpFull(d.mrr)}</Badge>
              </div>
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                {[['Pelanggan', d.usage?.customers], ['Penawaran', d.usage?.quotes], ['Proyek', d.usage?.projects], ['AI bln ini', d.usage?.ai_this_month]].map(([l, v]) => (
                  <div key={l} className="rounded-xl border border-border bg-background p-3">
                    <div className="text-[11px] text-muted-foreground">{l}</div>
                    <div className="mt-1 font-display text-lg font-bold tabular-nums">{v ?? 0}</div>
                  </div>
                ))}
              </div>
              <div className="rounded-xl border border-border bg-background p-3" data-testid="admin-tenant-ai-cost">
                <div className="flex items-center justify-between">
                  <span className="flex items-center gap-1.5 text-[11px] text-muted-foreground"><Cpu className="h-3.5 w-3.5" /> Biaya AI (bln ini)</span>
                  <span className="text-sm font-semibold tabular-nums">{rpFull(d.ai_cost_idr)} <span className="text-[11px] font-normal text-muted-foreground">({d.ai_calls || 0}×)</span></span>
                </div>
                {(d.ai_model_breakdown || []).slice(0, 5).map((m, i) => (
                  <div key={`${m.model}-${m.task}-${i}`} className="mt-1.5 flex items-center justify-between text-[11px] text-muted-foreground">
                    <span className="truncate pr-2">{TASK_LABEL[m.task] || m.task} · {m.model}</span>
                    <span className="shrink-0 tabular-nums">{m.calls}× · {rpCompact(m.cost_idr)}</span>
                  </div>
                ))}
              </div>
              <div className="text-xs text-muted-foreground">Owner: {d.members?.find((m) => m.role === 'owner')?.email || d.owner_email || '-'}</div>
            </TabsContent>
            <TabsContent value="anggota" className="pt-3">
              <div className="space-y-2">
                {(d.members || []).map((m) => (
                  <div key={m.id} className="flex items-center justify-between rounded-xl border border-border bg-background p-3">
                    <div><div className="text-sm font-medium">{m.nama}</div><div className="text-xs text-muted-foreground">{m.email}</div></div>
                    <Badge variant="outline" className="rounded-full capitalize">{m.role}</Badge>
                  </div>
                ))}
              </div>
            </TabsContent>
            <TabsContent value="langganan" className="space-y-4 pt-3">
              <div>
                <div className="mb-1.5 text-xs font-medium text-muted-foreground">Ubah paket (aktivasi manual setelah pembayaran)</div>
                <div className="flex flex-wrap gap-2">
                  {['free', 'pro', 'business'].map((p) => (
                    <Button key={p} size="sm" disabled={busy || d.plan === p} onClick={() => setPlan(p)}
                      data-testid={`admin-set-plan-${p}`} variant={d.plan === p ? 'default' : 'outline'} className="rounded-lg">
                      {d.plan === p && <BadgeCheck className="mr-1.5 h-4 w-4" />}{PLAN_LABEL[p]}
                    </Button>
                  ))}
                </div>
              </div>
              {d.requested_plan && (
                <div className="rounded-xl border border-amber-500/30 bg-amber-500/10 p-3 text-xs text-amber-300">
                  Pelanggan minta upgrade ke <b className="capitalize">{d.requested_plan}</b>. Klik paket di atas untuk mengaktifkan.
                </div>
              )}
              <div>
                <div className="mb-1.5 text-xs font-medium text-muted-foreground">Status akun</div>
                <Button size="sm" variant="outline" disabled={busy} onClick={toggleSuspend} data-testid="admin-toggle-suspend"
                  className={d.suspended ? 'rounded-lg border-emerald-500/40 text-emerald-400' : 'rounded-lg border-red-500/40 text-red-400'}>
                  {d.suspended ? <><ShieldCheck className="mr-1.5 h-4 w-4" /> Aktifkan kembali</> : <><ShieldOff className="mr-1.5 h-4 w-4" /> Tangguhkan workshop</>}
                </Button>
              </div>

              <div>
                <div className="mb-1.5 text-xs font-medium text-muted-foreground">Kuota AI / bulan</div>
                <div className="space-y-2.5 rounded-xl border border-border bg-background p-3">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Pemakaian bln ini</span>
                    <span className="font-medium tabular-nums" data-testid="admin-ai-usage">
                      {d.usage?.ai_this_month ?? 0} / {d.ai_limit === -1 ? '∞' : d.ai_limit}
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Biaya AI bln ini</span>
                    <span className="font-medium tabular-nums">{rpFull(d.ai_cost_idr)} <span className="text-xs font-normal text-muted-foreground">({d.ai_calls || 0}×)</span></span>
                  </div>
                  <Separator />
                  <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
                    <div className="flex-1">
                      <label className="text-[11px] text-muted-foreground" htmlFor="ai-quota-input">
                        Batas khusus (kosong = ikut paket, -1 = tanpa batas)
                      </label>
                      <Input id="ai-quota-input" type="number" value={quotaInput} onChange={(e) => setQuotaInput(e.target.value)}
                        placeholder={`default paket: ${d.plan_info?.limits?.ai_per_month === -1 ? '∞' : d.plan_info?.limits?.ai_per_month}`}
                        data-testid="admin-ai-quota-input" className="mt-1" />
                    </div>
                    <div className="flex gap-2">
                      <Button size="sm" disabled={busy} onClick={saveQuota} data-testid="admin-ai-quota-save" className="rounded-lg">
                        <Save className="mr-1.5 h-4 w-4" /> Simpan
                      </Button>
                      <Button size="sm" variant="outline" disabled={busy} onClick={resetQuota} data-testid="admin-ai-quota-reset" className="rounded-lg">
                        <RotateCcw className="mr-1.5 h-4 w-4" /> Reset
                      </Button>
                    </div>
                  </div>
                  {d.ai_quota_override != null && (
                    <div className="text-[11px] text-cyan-300">
                      Batas khusus aktif: {d.ai_quota_override === -1 ? 'tanpa batas' : `${d.ai_quota_override}/bln`} (menimpa paket).
                    </div>
                  )}
                </div>
              </div>
            </TabsContent>
          </Tabs>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function AdminConsole() {
  const { user, logout } = useAuth();
  const [metrics, setMetrics] = useState(null);
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [planFilter, setPlanFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [detailId, setDetailId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (search) params.search = search;
      if (planFilter !== 'all') params.plan = planFilter;
      if (statusFilter !== 'all') params.status = statusFilter;
      const [m, t] = await Promise.all([
        api.get('/admin/metrics'),
        api.get('/admin/tenants', { params }),
      ]);
      setMetrics(m.data);
      setTenants(t.data.tenants);
    } catch { /* abaikan kegagalan muat data admin */ } finally { setLoading(false); }
  }, [search, planFilter, statusFilter]);

  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  return (
    <div className="admin-shell dark min-h-screen bg-background text-foreground">
      {/* Top bar */}
      <header className="sticky top-0 z-30 border-b border-border bg-card/80 backdrop-blur">
        <div className="mx-auto flex max-w-[1200px] items-center justify-between px-4 py-3 sm:px-6">
          <div className="flex items-center gap-2.5">
            <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary text-primary-foreground font-display text-lg font-bold">A</div>
            <div>
              <div className="font-display text-base font-semibold leading-tight">AluminaOne Admin</div>
              <div className="text-xs text-muted-foreground">Konsol Platform</div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Button size="icon" variant="ghost" onClick={load} aria-label="Muat ulang" data-testid="admin-refresh"><RefreshCw className="h-4 w-4" /></Button>
            <span className="hidden text-sm text-muted-foreground sm:inline">{user?.email}</span>
            <Button size="sm" variant="outline" onClick={logout} data-testid="admin-logout-button"><LogOut className="mr-1.5 h-4 w-4" /> Keluar</Button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-[1200px] space-y-6 px-4 py-6 sm:px-6">
        <div>
          <h1 className="font-display text-xl font-bold">Ringkasan Platform</h1>
          <p className="text-sm text-muted-foreground">Pantau semua workshop pelanggan, langganan, dan pendapatan.</p>
        </div>

        {/* KPI row */}
        {loading && !metrics ? (
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">{Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-24 rounded-2xl" />)}</div>
        ) : metrics && (
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
            <Kpi icon={Building2} label="Total Workshop" value={metrics.total_orgs} sub={`${metrics.paying_orgs} berbayar`} testid="admin-kpi-total" />
            <Kpi icon={Wallet} label="MRR" value={rpCompact(metrics.mrr)} sub={`ARR ${rpCompact(metrics.arr)}`} testid="admin-kpi-mrr" />
            <Kpi icon={CircleDollarSign} label="Langganan Aktif" value={metrics.status_count?.active || 0} sub={`${metrics.plan_count?.pro || 0} Pro · ${metrics.plan_count?.business || 0} Bisnis`} testid="admin-kpi-active" />
            <Kpi icon={Users} label="Trial Aktif" value={metrics.status_count?.trial || 0} sub={`${metrics.status_count?.expired || 0} expired`} testid="admin-kpi-trial" />
            <Kpi icon={TrendingUp} label="Workshop Baru (30h)" value={metrics.growth_30d} sub="pendaftaran" testid="admin-kpi-growth" />
          </div>
        )}

        {/* Unit economics — AI cost vs MRR */}
        {metrics && (
          <section data-testid="admin-unit-economics" className="rounded-2xl border border-border bg-card p-4 sm:p-5">
            <div className="mb-4 flex flex-wrap items-center gap-x-2 gap-y-1">
              <Cpu className="h-4 w-4 text-primary" />
              <h2 className="font-display text-base font-semibold">Unit Ekonomi AI</h2>
              <span className="text-xs text-muted-foreground">· estimasi biaya model vs MRR (bulan ini)</span>
            </div>
            <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
              <div className="rounded-xl border border-border bg-background p-4">
                <div className="flex items-center gap-2 text-xs text-muted-foreground"><Coins className="h-4 w-4" /> Biaya AI (bln ini)</div>
                <div className="mt-2 font-display text-2xl font-bold tabular-nums" data-testid="admin-ai-cost">{rpCompact(metrics.ai_cost_idr)}</div>
                <div className="mt-0.5 text-xs text-muted-foreground">{metrics.ai_calls || 0} panggilan · ${Number(metrics.ai_cost_usd || 0).toFixed(2)}</div>
              </div>
              <div className="rounded-xl border border-border bg-background p-4">
                <div className="flex items-center gap-2 text-xs text-muted-foreground"><Wallet className="h-4 w-4" /> MRR</div>
                <div className="mt-2 font-display text-2xl font-bold tabular-nums">{rpCompact(metrics.mrr)}</div>
                <div className="mt-0.5 text-xs text-muted-foreground">pendapatan langganan</div>
              </div>
              <div className="rounded-xl border border-border bg-background p-4">
                <div className="flex items-center gap-2 text-xs text-muted-foreground"><TrendingUp className="h-4 w-4" /> Gross Margin</div>
                <div className="mt-2 font-display text-2xl font-bold tabular-nums" data-testid="admin-gross-margin">{rpCompact(metrics.gross_margin)}</div>
                <div className="mt-0.5 text-xs text-muted-foreground">MRR − biaya AI</div>
              </div>
              <div className="rounded-xl border border-border bg-background p-4">
                <div className="flex items-center gap-2 text-xs text-muted-foreground"><Percent className="h-4 w-4" /> Margin Kotor</div>
                <div className={`mt-2 font-display text-2xl font-bold tabular-nums ${marginTone(metrics.margin_pct)}`} data-testid="admin-margin-pct">{metrics.margin_pct}%</div>
                <div className="mt-0.5 text-xs text-muted-foreground">$1 = Rp {Number(metrics.usd_idr || 0).toLocaleString('id-ID')}</div>
              </div>
            </div>

            {metrics.model_breakdown?.length > 0 ? (
              <div className="mt-4">
                <div className="mb-2 text-xs font-medium text-muted-foreground">Rincian per model &amp; tugas</div>
                <div className="grid gap-2 sm:grid-cols-2">
                  {metrics.model_breakdown.slice(0, 8).map((m, i) => (
                    <div key={`${m.model}-${m.task}-${i}`} className="flex items-center justify-between rounded-lg border border-border bg-background px-3 py-2" data-testid="admin-model-breakdown-row">
                      <div className="min-w-0">
                        <div className="truncate text-sm font-medium">{TASK_LABEL[m.task] || m.task}</div>
                        <div className="truncate text-[11px] text-muted-foreground">{m.model}</div>
                      </div>
                      <div className="text-right">
                        <div className="text-sm tabular-nums">{rpCompact(m.cost_idr)}</div>
                        <div className="text-[11px] tabular-nums text-muted-foreground">{m.calls}×</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="mt-4 text-xs text-muted-foreground">Belum ada pemakaian AI tercatat bulan ini.</div>
            )}
          </section>
        )}

        {/* Tenant table */}
        <div className="rounded-2xl border border-border bg-card">
          <div className="flex flex-col gap-3 border-b border-border p-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="relative w-full sm:max-w-xs">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input data-testid="admin-tenant-search-input" placeholder="Cari workshop…" value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" />
            </div>
            <div className="flex gap-2">
              <Select value={planFilter} onValueChange={setPlanFilter}>
                <SelectTrigger className="w-[130px]" data-testid="admin-filter-plan"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Semua Paket</SelectItem>
                  <SelectItem value="free">Gratis</SelectItem>
                  <SelectItem value="pro">Pro</SelectItem>
                  <SelectItem value="business">Bisnis</SelectItem>
                </SelectContent>
              </Select>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-[130px]" data-testid="admin-filter-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Semua Status</SelectItem>
                  <SelectItem value="trial">Trial</SelectItem>
                  <SelectItem value="active">Aktif</SelectItem>
                  <SelectItem value="expired">Expired</SelectItem>
                  <SelectItem value="suspended">Suspended</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="overflow-x-auto">
            <Table data-testid="admin-tenant-table">
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead>Workshop</TableHead>
                  <TableHead>Owner</TableHead>
                  <TableHead>Paket</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">MRR</TableHead>
                  <TableHead className="text-right">Biaya AI</TableHead>
                  <TableHead className="text-center">Tim</TableHead>
                  <TableHead className="w-10" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  Array.from({ length: 4 }).map((_, i) => (
                    <TableRow key={i}><TableCell colSpan={8}><Skeleton className="h-8 w-full" /></TableCell></TableRow>
                  ))
                ) : tenants.length === 0 ? (
                  <TableRow><TableCell colSpan={8} className="py-10 text-center text-sm text-muted-foreground">Belum ada workshop yang cocok.</TableCell></TableRow>
                ) : tenants.map((t) => (
                  <TableRow key={t.id} data-testid="admin-tenant-row" className="cursor-pointer" onClick={() => setDetailId(t.id)}>
                    <TableCell>
                      <div className="font-medium">{t.nama}</div>
                      {t.requested_plan && <span className="text-[11px] text-amber-400">minta upgrade → {t.requested_plan}</span>}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">{t.owner_email || '-'}</TableCell>
                    <TableCell><Badge variant="outline" className={`rounded-full ${PLAN_BADGE[t.plan]}`}>{PLAN_LABEL[t.plan]}</Badge></TableCell>
                    <TableCell><Badge variant="outline" className={`rounded-full ${STATUS_BADGE[t.status]}`}>{STATUS_LABEL[t.status]}</Badge></TableCell>
                    <TableCell className="text-right tabular-nums">{t.mrr ? rpFull(t.mrr) : '—'}</TableCell>
                    <TableCell className="text-right tabular-nums" data-testid="admin-tenant-ai-cost-cell">
                      {t.ai_cost_idr ? rpCompact(t.ai_cost_idr) : '—'}
                      {t.ai_calls ? <span className="ml-1 text-[11px] text-muted-foreground">{t.ai_calls}×</span> : null}
                    </TableCell>
                    <TableCell className="text-center tabular-nums">{t.members}</TableCell>
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button size="icon" variant="ghost" data-testid="admin-tenant-row-action-menu"><MoreHorizontal className="h-4 w-4" /></Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuLabel>Aksi</DropdownMenuLabel>
                          <DropdownMenuItem onClick={() => setDetailId(t.id)}>Lihat detail</DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem onClick={() => setDetailId(t.id)}>Ubah paket / status</DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      </main>

      <TenantDetailDialog orgId={detailId} open={!!detailId} onClose={() => setDetailId(null)} onChanged={load} />
    </div>
  );
}
